import { useWizardStore } from '@/store/wizardStore'
import type { RobotRole } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step1RoleProps { lang: Lang }

const ROLES: {
  id: RobotRole
  icon: string
  accent: 'primary' | 'secondary'
  fr: { title: string; desc: string }
  en: { title: string; desc: string }
}[] = [
  {
    id: 'accueil',
    icon: 'hail',
    accent: 'primary',
    fr: { title: 'Accueil / Orientation', desc: 'Interaction client fluide, guidage dans les complexes et gestion des flux de visiteurs.' },
    en: { title: 'Reception / Guidance', desc: 'Smooth client interaction, complex navigation and visitor flow management.' },
  },
  {
    id: 'assistance_personnelle',
    icon: 'smart_toy',
    accent: 'secondary',
    fr: { title: 'Assistance personnelle', desc: 'Compagnon intelligent pour l\'organisation quotidienne, les rappels et le support cognitif.' },
    en: { title: 'Personal Assistance', desc: 'Intelligent companion for daily organization, reminders and cognitive support.' },
  },
  {
    id: 'securite',
    icon: 'security',
    accent: 'primary',
    fr: { title: 'Sécurité / Surveillance', desc: 'Patrouilles autonomes, détection d\'anomalies et monitoring thermique 24/7.' },
    en: { title: 'Security / Surveillance', desc: 'Autonomous patrols, anomaly detection and 24/7 thermal monitoring.' },
  },
  {
    id: 'retail',
    icon: 'shopping_bag',
    accent: 'primary',
    fr: { title: 'Retail / Vente', desc: 'Optimisation de l\'inventaire, aide au choix et expérience shopping immersive.' },
    en: { title: 'Retail / Sales', desc: 'Inventory optimization, purchase guidance and immersive shopping experience.' },
  },
  {
    id: 'aide_domicile',
    icon: 'home_health',
    accent: 'primary',
    fr: { title: 'Aide à domicile', desc: 'Assistance à la mobilité, gestion domestique et soutien aux seniors.' },
    en: { title: 'Home Care', desc: 'Mobility assistance, domestic management and elderly support.' },
  },
  {
    id: 'evenementiel',
    icon: 'celebration',
    accent: 'secondary',
    fr: { title: 'Événementiel', desc: 'Animation interactive, service de boissons et capture média haute définition.' },
    en: { title: 'Events', desc: 'Interactive entertainment, beverage service and high-definition media capture.' },
  },
  {
    id: 'industrie_legere',
    icon: 'precision_manufacturing',
    accent: 'primary',
    fr: { title: 'Industrie légère', desc: 'Logistique interne, manipulation de précision et contrôle qualité automatisé.' },
    en: { title: 'Light Industry', desc: 'Internal logistics, precision manipulation and automated quality control.' },
  },
  {
    id: 'sur_mesure',
    icon: 'architecture',
    accent: 'primary',
    fr: { title: 'Sur mesure', desc: 'Développement de fonctionnalités spécifiques pour des besoins uniques.' },
    en: { title: 'Custom', desc: 'Development of specific capabilities for unique requirements.' },
  },
]

const SELECT_LABEL = { fr: 'Sélectionner', en: 'Select' }

export default function Step1Role({ lang }: Step1RoleProps) {
  const { draft, update } = useWizardStore()
  const selected = draft.step1_role

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {ROLES.map((role) => {
        const isSelected = selected === role.id
        const accentClass = role.accent === 'secondary' ? 'text-secondary' : 'text-primary'
        const hoverTitle = role.accent === 'secondary' ? 'group-hover:text-secondary' : 'group-hover:text-primary'
        const selectedRing = isSelected
          ? 'ring-1 ring-primary-container bg-surface-container-highest/80'
          : ''

        return (
          <div
            key={role.id}
            onClick={() => update({ step1_role: role.id })}
            className={`glass-card p-6 rounded-xl border border-outline-variant/20 flex flex-col group cursor-pointer ${selectedRing}`}
          >
            <div className={`w-12 h-12 bg-surface-container-highest rounded-lg flex items-center justify-center mb-6 ${accentClass} shadow-lg`}>
              <span className="material-symbols-outlined text-3xl">{role.icon}</span>
            </div>
            <h3 className={`font-headline text-xl font-bold mb-2 transition-colors ${isSelected ? accentClass : hoverTitle}`}>
              {role[lang].title}
            </h3>
            <p className="text-sm text-on-surface-variant leading-relaxed mb-8">
              {role[lang].desc}
            </p>
            <div className={`mt-auto flex items-center text-xs font-bold tracking-widest uppercase transition-colors ${isSelected ? accentClass : `${accentClass}/60 ${role.accent === 'secondary' ? 'group-hover:text-secondary' : 'group-hover:text-primary'}`}`}>
              {SELECT_LABEL[lang]}
              <span className="material-symbols-outlined ml-2 text-sm">
                {isSelected ? 'check_circle' : 'arrow_forward'}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
